import { type NoteEditorSurface } from '@/components/tiptap/telemetry/emit-note-editor-render-failed';
import { applyHtmlContentPreservingSelection } from '@/components/tiptap/utils/applyContentPreservingSelection';
import { type NoteEditorType } from '@/models/analytics/features/notes';
import { isEditorLive } from '@heidi/editor/core';
import { type Editor } from '@tiptap/react';
import { useEffect, useRef } from 'react';

/**
 * Syncs editor content when the content prop changes.
 * Compares current content before updating to prevent feedback loops.
 * Preserves the current selection position when updating content.
 *
 * When `isStreaming` is true, updates are throttled to one per animation frame
 * to avoid overwhelming the editor with rapid `setContent` calls during SSE streaming.
 * When streaming ends, the final content is applied immediately.
 *
 * When `suppressEchoWhileFocused` is true, a non-streaming content change is
 * skipped while the editor is focused and editable — that change is almost
 * always the echo of the user's own debounced save round-tripping through the
 * markdown serializer, and re-applying it would reset the cursor mid-type.
 * Generated content (non-editable) and streamed updates (the `isStreaming`
 * path) still apply. A suppressed echo is deliberately not re-applied on blur:
 * the editor holds the latest edits and NoteTabPane flushes them, so
 * re-applying the prop could revert an edit that hasn't round-tripped yet.
 *
 * @param params.editor - The Tiptap editor instance
 * @param params.content - The HTML content to sync
 * @param params.editorVariant - Engine this editor runs, for render-failure telemetry
 * @param params.surface - Note-tab SLI membership; only the note tab supplies it
 * @param params.isStreaming - Whether content is actively being streamed (throttles updates)
 * @param params.suppressEchoWhileFocused - Skip non-streaming sync while the editor is focused
 */
export const useEditorContentSync = ({
  editor,
  content,
  editorVariant,
  surface,
  isStreaming = false,
  suppressEchoWhileFocused = false,
}: {
  editor: Editor | null | undefined;
  content: string;
  editorVariant: NoteEditorType;
  surface?: NoteEditorSurface;
  isStreaming?: boolean;
  suppressEchoWhileFocused?: boolean;
}) => {
  const pendingContentRef = useRef<string | null>(null);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (isEditorLive(editor, 'useEditorContentSync')) {
      const currentHtml = editor.getHTML();

      if (currentHtml !== content) {
        if (isStreaming) {
          pendingContentRef.current = content;
          if (rafIdRef.current === null) {
            rafIdRef.current = requestAnimationFrame(() => {
              rafIdRef.current = null;
              if (
                pendingContentRef.current !== null &&
                isEditorLive(editor, 'useEditorContentSync.raf')
              ) {
                applyHtmlContentPreservingSelection(editor, pendingContentRef.current, {
                  editorVariant,
                  surface,
                });
                pendingContentRef.current = null;
              }
            });
          }
        } else if (!(suppressEchoWhileFocused && editor.isFocused && editor.isEditable)) {
          // Not streaming — cancel any pending throttled update and apply immediately.
          if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
          }
          pendingContentRef.current = null;
          applyHtmlContentPreservingSelection(editor, content, {
            editorVariant,
            surface,
          });
        }
      }
    }

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [content, editor, editorVariant, surface, isStreaming, suppressEchoWhileFocused]);
};
